import { uniq } from "es-toolkit";

import { type HeaderType } from "./commands";
import { getHeaderCells } from "./utils";

import type { CellVerticalAlignment } from "./TableCellVerticalAlignment";
import type { HeaderStyle } from "./types";
import type { Alignment } from "@/components/doc/right-sidebar/align-dropdown/AlignDropdown";
import type { Color, FontSize } from "@corca/data";
import type { Node } from "@tiptap/pm/model";

export function getHeaderStyles(tableNode: Node, type: HeaderType): HeaderStyle | null {
  const headerCells = getHeaderCells(tableNode, type).map(({ node }) => node);

  if (headerCells.length === 0) {
    return null;
  }

  return {
    size: getFontSize(headerCells),
    color: getFontColor(headerCells),
    backgroundColor: getBackgroundColor(headerCells),
    alignment: getAlignment(headerCells),
    verticalAlignment: getCellVerticalAlignment(headerCells),
  };
}

function getFontSize(headerCells: Node[]): FontSize | undefined {
  const availableSizes = headerCells.reduce<FontSize[]>((acc, cell) => {
    const sizes = getHeaderCellTextStyleValues<FontSize>(cell, "size", "16");

    if (sizes.length > 0) {
      acc.push(...sizes);
    }

    return acc;
  }, []);

  const sizes = uniq(availableSizes).slice(0, 4);

  if (sizes.length > 1) {
    return undefined;
  }

  if (sizes.length === 1) {
    return sizes[0];
  }

  return "16";
}

function getFontColor(headerCells: Node[]): Color[] {
  const availableColors = headerCells.reduce<Color[]>((acc, cell) => {
    const colors = getHeaderCellTextStyleValues<Color>(cell, "color", "black");

    if (colors.length > 0) {
      acc.push(...colors);
    }

    return acc;
  }, []);

  return uniq(availableColors).slice(0, 4);
}

function getBackgroundColor(headerCells: Node[]): Color[] {
  return uniq(
    headerCells.map((cell) => {
      return (cell.attrs.backgroundColor || "white") as Color;
    }),
  ).slice(0, 4);
}

function getCellVerticalAlignment(headerCells: Node[]): CellVerticalAlignment | undefined {
  const alignments = uniq(
    headerCells.map((cell) => {
      return (cell.attrs.verticalAlignment || "top") as CellVerticalAlignment;
    }),
  ).slice(0, 2);

  if (alignments.length > 1) {
    return undefined;
  }

  if (alignments.length === 1) {
    return alignments[0];
  }

  return undefined;
}

function getAlignment(headerCells: Node[]): Alignment | undefined {
  const alignments = uniq(
    headerCells.map((cell) => {
      return (cell.attrs.alignment || "left") as Alignment;
    }),
  ).slice(0, 2);

  if (alignments.length > 1) {
    return undefined;
  }

  if (alignments.length === 1) {
    return alignments[0];
  }

  return undefined;
}

function getHeaderCellTextStyleValues<T extends Color | FontSize>(
  headerCell: Node,
  attrKey: string,
  defaultValue?: T,
): T[] {
  const marks: T[] = [];

  headerCell.descendants((childNode) => {
    const childNodeMarks = childNode.marks.filter(
      (mark) => mark.type.name === "textStyle" && mark.attrs[attrKey],
    );

    if (childNodeMarks.length > 0) {
      marks.push(...childNodeMarks.map((mark) => mark.attrs[attrKey] as T));
    }
  });

  if (marks.length > 0) {
    return uniq(marks);
  }

  if (headerCell.attrs[attrKey]) {
    return [headerCell.attrs[attrKey] as T];
  }

  if (defaultValue) {
    return [defaultValue];
  }

  return [];
}
